// src/components/common/AutoMarquee.jsx
import { useEffect, useMemo, useRef, useState } from "react";

/**
 * props:
 *  - children: string | ReactNode
 *  - speed: px/sec (기본 80)
 *  - gap: 반복 간격(px, 기본 24)
 *  - className: 추가 클래스
 *  - reserveRight: 우측 예약폭(px) — X 버튼 영역을 비워두기 위해 사용 (기본 0)
 *  - reserveLeft: 좌측 예약폭(px) — 필요시 사용 (기본 0)
 *  - maxInline: 내용 최대 폭 제한 (예: "36rem") — 선택
 */
export default function AutoMarquee({
    children,
    speed = 50,
    gap = 24,
    className = "",
    reserveRight = 0,
    reserveLeft = 0,
    maxInline, // ex) "36rem"
}) {
    const wrapRef = useRef(null);
    const itemRef = useRef(null);
    const [needScroll, setNeedScroll] = useState(false);
    const [distance, setDistance] = useState(0);
    const [duration, setDuration] = useState(0);
    const [paused, setPaused] = useState(false);

    const reduceMotion = useMemo(() => {
        if (typeof window === "undefined") return false;
        return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
    }, []);

    // 공통 스타일: 예약폭을 합산해 실 너비를 제한
    const reserved = Math.max(0, reserveLeft + reserveRight);
    const outerStyleBase = {
        maxWidth: reserved ? `calc(100% - ${reserved}px)` : undefined,
        marginInline: "auto",             // 중앙 정렬
        ...(maxInline ? {
            width: "100%", maxWidth: reserved
                ? `min(${maxInline}, calc(100% - ${reserved}px))`
                : maxInline
        } : {}),
    };

    useEffect(() => {
        function measure() {
            const wrap = wrapRef.current;
            const item = itemRef.current;
            if (!wrap || !item) return;

            const wrapW = wrap.clientWidth;
            const itemW = item.scrollWidth;

            setNeedScroll(itemW > wrapW + 1);
            const d = itemW + gap;
            setDistance(d);
            setDuration(Math.max(0.001, d / speed));
        }

        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [children, speed, gap, reserved, maxInline]);

    if (reduceMotion) {
        return (
            <div className={`w-full overflow-hidden ${className}`} style={outerStyleBase}>
                <div className="truncate whitespace-nowrap">{children}</div>
            </div>
        );
    }

    if (!needScroll) {
        return (
            <div className={`w-full overflow-hidden ${className}`} ref={wrapRef} style={outerStyleBase}>
                <div ref={itemRef} className="truncate whitespace-nowrap">
                    {children}
                </div>
            </div>
        );
    }

    return (
        <div
            className={`w-full overflow-hidden ${className}`}
            ref={wrapRef}
            style={outerStyleBase}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <style>
                {`
        @keyframes marquee-x {
          from { transform: translateX(0); }
          to   { transform: translateX(calc(-1 * var(--marquee-distance))); }
        }
        `}
            </style>
            <div
                className="flex items-center whitespace-nowrap"
                style={{
                    ["--marquee-distance"]: `${distance}px`,
                    animation: `marquee-x ${duration}s linear infinite`,
                    animationPlayState: paused ? "paused" : "running",
                }}
            >
                <div ref={itemRef} className="pr-[var(--gap)]" style={{ ["--gap"]: `${gap}px` }}>
                    {children}
                </div>
                <div className="pr-[var(--gap)]" style={{ ["--gap"]: `${gap}px` }}>
                    {children}
                </div>
            </div>
        </div>
    );
}
